export const SHARE_RESULT_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
export const SHARE_RESULT_MAX_RESPONSE_BYTES = 64 * 1024;
export const SHARE_RESULT_MAX_EQUITY_POINTS = 48;

export const sharedResultLocales = [
  "en",
  "vi",
  "hi",
  "ur",
  "th",
  "bn",
  "id",
  "fil",
  "es-419",
  "pt-BR",
  "ja",
  "ko",
  "ru",
  "tr",
  "uk",
] as const;

export type SharedResultLocale = (typeof sharedResultLocales)[number];

export type SharedEvidenceState =
  | "unvalidated"
  | "in_sample"
  | "selection_passed"
  | "locked_holdout_passed"
  | "locked_holdout_failed"
  | "insufficient_evidence"
  | "unknown";

export type SharedEquityPoint = {
  progress: number;
  returnPercent: number;
};

export type SharedStrategyReproduction = {
  provider?: string;
  market?: string;
  schemaVersion: 1;
  strategyDSL: Record<string, unknown>;
  strategyLabel: string;
  symbol: string;
  timeframe: string;
  assumptions: {
    commissionPercent: number;
    slippageBps: number;
    positionSizePercent: number;
    executionModel: "spot" | "futures";
    marginMode: "isolated" | "cross";
    leverage: number;
    maintenanceMarginPercent: number;
    fundingRatePercent: number;
    fundingIntervalHours: number;
    liquidationFeePercent: number;
    tradeDirection: "long" | "short" | "both";
    exitMode: "tp_sl_only" | "signal_and_risk" | "signal_only";
    stopLossPercent: number;
    takeProfitPercent: number;
    period: "1M" | "3M" | "6M" | "1Y" | "Custom";
    startDate: string;
    endDate: string;
  };
};

/**
 * Browser/edge-safe allow-list. Deliberately excludes account identifiers,
 * strategy rules/prompts, fingerprints, notes, raw candles, and raw trades.
 */
export type PublicSharedResult = {
  mode: "summary_only" | "reproducible_strategy";
  researchNetworkEnabled: boolean;
  moderationEnabled: boolean;
  shareID?: string;
  locale: SharedResultLocale;
  createdAt: string;
  expiresAt: string;
  resultCreatedAt: string;
  strategyLabel: string;
  symbol: string;
  timeframe: string;
  testedPeriod: {
    start: string | null;
    end: string | null;
  };
  evidenceState: SharedEvidenceState;
  metrics: {
    totalReturnPercent: number | null;
    maxDrawdownPercent: number | null;
    winRatePercent: number | null;
    profitFactor: number | null;
    totalTrades: number;
  };
  equityCurve: SharedEquityPoint[];
  methodologyURL: string;
  riskNote: string;
  reproduction?: SharedStrategyReproduction;
};

export type SharedResultLoadState =
  | { kind: "ready"; result: PublicSharedResult }
  | { kind: "not_found" }
  | { kind: "expired" }
  | { kind: "service_unavailable" };

export type SharedResultBootstrap = {
  schemaVersion: 1;
  state: SharedResultLoadState;
};

type SharedResultLocaleStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
};

export function readStoredSharedResultLocale(
  storage: () => SharedResultLocaleStorage,
): string | null {
  try {
    return storage().getItem("stoptrading.locale");
  } catch {
    return null;
  }
}

export function writeStoredSharedResultLocale(
  storage: () => SharedResultLocaleStorage,
  value: SharedResultLocale,
): void {
  try {
    storage().setItem("stoptrading.locale", value);
  } catch {
    // Storage can be unavailable in hardened/private browser contexts. The
    // viewer remains fully usable for the current session without persistence.
  }
}

const localeSet = new Set<string>(sharedResultLocales);
const evidenceStates = new Set<SharedEvidenceState>([
  "unvalidated",
  "in_sample",
  "selection_passed",
  "locked_holdout_passed",
  "locked_holdout_failed",
  "insufficient_evidence",
  "unknown",
]);
const requiredOmittedFields = new Set([
  "account_and_auth_data",
  "backend_and_repository_ids",
  "exact_strategy_rules_and_prompt",
  "fingerprints_and_private_notes",
  "raw_candles_and_trades",
  "starting_capital_and_usage_data",
]);
const unsafeUnicodePattern = /[\p{Cc}\p{Cf}\p{Cs}]/u;
const urlOrEmailPattern = /(?:https?:\/\/|www\.|[^\s@]+@[^\s@]+\.[^\s@]+)/iu;
const safeRuleIDPattern = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,79}$/;
const safeCatalogVersionPattern = /^\d{4}-\d{2}-\d{2}$/;
const safeShareIDPattern = /^shr_[A-Za-z0-9_-]{43}$/;
const strategyOperators = new Set([
  "gt", "gte", "lt", "lte", "crosses_above", "crosses_below", "rising", "falling",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function safeString(value: unknown, maximumCodePoints: number): string | null {
  if (typeof value !== "string") return null;
  if (unsafeUnicodePattern.test(value)) return null;
  const clean = value.trim();
  if (!clean || Array.from(clean).length > maximumCodePoints) return null;
  return clean;
}

function safeNarrativeString(value: unknown, maximumCodePoints: number): string | null {
  const clean = safeString(value, maximumCodePoints);
  if (!clean || urlOrEmailPattern.test(clean)) return null;
  return clean;
}

function finiteNumber(value: unknown, minimum: number, maximum: number): number | null | undefined {
  if (value === null) return null;
  if (typeof value !== "number" || !Number.isFinite(value) || value < minimum || value > maximum) {
    return undefined;
  }
  return value;
}

function integer(value: unknown, minimum: number, maximum: number): number | undefined {
  if (!Number.isSafeInteger(value) || (value as number) < minimum || (value as number) > maximum) {
    return undefined;
  }
  return value as number;
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length
    && actual.every((key, index) => key === expected[index]);
}

function parseSharedStrategyDSL(value: unknown): Record<string, unknown> | null {
  if (!isRecord(value)
    || !exactKeys(value, ["schema_version", "catalog_version", "short_mode", "entry", "exit", "risk"])
    || value.schema_version !== 2
    || typeof value.catalog_version !== "string"
    || !safeCatalogVersionPattern.test(value.catalog_version)
    || value.short_mode !== "mirror_long"
    || !isRecord(value.risk)
    || !exactKeys(value.risk, ["mode"])
    || value.risk.mode !== "inherit") return null;
  const seen = new Set<string>();
  const parseOperand = (operand: unknown, allowNumber: boolean): Record<string, unknown> | null => {
    if (!isRecord(operand)) return null;
    if (operand.kind === "number") {
      if (!allowNumber || !exactKeys(operand, ["kind", "value"])
        || typeof operand.value !== "number" || !Number.isFinite(operand.value)
        || Math.abs(operand.value) > 1_000_000_000) return null;
      return { kind: "number", value: operand.value };
    }
    if (operand.kind !== "indicator" || !exactKeys(operand, ["kind", "id", "parameters"])
      || typeof operand.id !== "string" || !safeRuleIDPattern.test(operand.id)
      || !isRecord(operand.parameters) || Object.keys(operand.parameters).length > 12) return null;
    const parameters: Record<string, number> = {};
    for (const [key, parameter] of Object.entries(operand.parameters)) {
      if (!safeRuleIDPattern.test(key) || typeof parameter !== "number"
        || !Number.isFinite(parameter) || Math.abs(parameter) > 1_000_000_000) return null;
      parameters[key] = parameter;
    }
    return { kind: "indicator", id: operand.id, parameters };
  };
  const parseGroup = (group: unknown, id: "entry" | "exit"): Record<string, unknown> | null => {
    if (!isRecord(group) || !exactKeys(group, ["id", "match", "conditions"])
      || group.id !== id || (group.match !== "all" && group.match !== "any")
      || !Array.isArray(group.conditions) || group.conditions.length > 12
      || (id === "entry" && group.conditions.length === 0)) return null;
    const conditions: Record<string, unknown>[] = [];
    for (const condition of group.conditions) {
      if (!isRecord(condition) || typeof condition.id !== "string"
        || !safeRuleIDPattern.test(condition.id) || seen.has(condition.id)
        || typeof condition.operator !== "string" || !strategyOperators.has(condition.operator)) return null;
      const unary = condition.operator === "rising" || condition.operator === "falling";
      if (!exactKeys(condition, unary
        ? ["id", "left", "operator"]
        : ["id", "left", "operator", "right"])) return null;
      const left = parseOperand(condition.left, false);
      const right = unary ? undefined : parseOperand(condition.right, true);
      if (!left || (!unary && !right)) return null;
      seen.add(condition.id);
      conditions.push({
        id: condition.id,
        left,
        operator: condition.operator,
        ...(right ? { right } : {}),
      });
    }
    return { id, match: group.match, conditions };
  };
  const entry = parseGroup(value.entry, "entry");
  const exit = parseGroup(value.exit, "exit");
  if (!entry || !exit) return null;
  return {
    schema_version: 2,
    catalog_version: value.catalog_version,
    short_mode: "mirror_long",
    entry,
    exit,
    risk: { mode: "inherit" },
  };
}

function parseReproduction(value: unknown): SharedStrategyReproduction | null {
  if (!isRecord(value)
    || !exactKeys(value, ["schema_version", "strategy_dsl", "strategy_label", "symbol", "timeframe", "assumptions", ...("provider" in value ? ["provider"] : []), ...("market" in value ? ["market"] : [])])
    || value.schema_version !== 1 || !isRecord(value.assumptions)) return null;
  if ((value.provider != null && !["auto", "binance", "binance_futures", "yfinance", "yahoo"].includes(String(value.provider)))
    || (value.market != null && !["auto", "crypto", "crypto_futures", "forex", "futures", "metal", "equity", "index", "futures_index"].includes(String(value.market)))) return null;
  const strategyDSL = parseSharedStrategyDSL(value.strategy_dsl);
  const strategyLabel = safeNarrativeString(value.strategy_label, 72);
  const symbol = safeString(value.symbol, 32);
  const timeframe = safeString(value.timeframe, 32);
  const a = value.assumptions;
  if (!strategyDSL || !strategyLabel || !symbol || !timeframe
    || !exactKeys(a, [
      "commission_percent", "slippage_bps", "position_size_percent",
      "execution_model", "margin_mode", "leverage", "maintenance_margin_percent",
      "funding_rate_percent", "funding_interval_hours", "liquidation_fee_percent",
      "trade_direction", "exit_mode", "stop_loss_percent", "take_profit_percent",
      "period", "start_date", "end_date",
    ])) return null;
  const commissionPercent = finiteNumber(a.commission_percent, 0, 5);
  const slippageBps = finiteNumber(a.slippage_bps, 0, 500);
  const positionSizePercent = finiteNumber(a.position_size_percent, Number.MIN_VALUE, 100);
  const leverage = finiteNumber(a.leverage, 1, 125);
  const maintenanceMarginPercent = finiteNumber(a.maintenance_margin_percent, 0, 100);
  const fundingRatePercent = finiteNumber(a.funding_rate_percent, -100, 100);
  const fundingIntervalHours = integer(a.funding_interval_hours, 1, 168);
  const liquidationFeePercent = finiteNumber(a.liquidation_fee_percent, 0, 100);
  const stopLossPercent = finiteNumber(a.stop_loss_percent, Number.MIN_VALUE, 50);
  const takeProfitPercent = finiteNumber(a.take_profit_percent, Number.MIN_VALUE, 100);
  const startDate = isoDate(a.start_date);
  const endDate = isoDate(a.end_date);
  if ([
    commissionPercent, slippageBps, positionSizePercent, leverage,
    maintenanceMarginPercent, fundingRatePercent, fundingIntervalHours,
    liquidationFeePercent, stopLossPercent, takeProfitPercent,
  ].some((item) => item === undefined || item === null)
    || (a.execution_model !== "spot" && a.execution_model !== "futures")
    || (a.margin_mode !== "isolated" && a.margin_mode !== "cross")
    || !["long", "short", "both"].includes(String(a.trade_direction))
    || !["tp_sl_only", "signal_and_risk", "signal_only"].includes(String(a.exit_mode))
    || !["1M", "3M", "6M", "1Y", "Custom"].includes(String(a.period))
    || !startDate || !endDate || Date.parse(endDate) < Date.parse(startDate)) return null;
  if (Number(maintenanceMarginPercent) >= 100
    || Number(fundingRatePercent) <= -100 || Number(fundingRatePercent) >= 100
    || Number(liquidationFeePercent) >= 100) return null;
  if (a.execution_model === "spot" && (leverage !== 1 || fundingRatePercent !== 0)) return null;
  if (a.execution_model === "futures"
    && Number(maintenanceMarginPercent) >= 100 / Number(leverage)) return null;
  return {
    schemaVersion: 1,
    ...(value.provider != null ? { provider: value.provider as string } : {}),
    ...(value.market != null ? { market: value.market as string } : {}),
    strategyDSL,
    strategyLabel,
    symbol,
    timeframe,
    assumptions: {
      commissionPercent: Number(commissionPercent),
      slippageBps: Number(slippageBps),
      positionSizePercent: Number(positionSizePercent),
      executionModel: a.execution_model,
      marginMode: a.margin_mode,
      leverage: Number(leverage),
      maintenanceMarginPercent: Number(maintenanceMarginPercent),
      fundingRatePercent: Number(fundingRatePercent),
      fundingIntervalHours: Number(fundingIntervalHours),
      liquidationFeePercent: Number(liquidationFeePercent),
      tradeDirection: a.trade_direction as "long" | "short" | "both",
      exitMode: a.exit_mode as "tp_sl_only" | "signal_and_risk" | "signal_only",
      stopLossPercent: Number(stopLossPercent),
      takeProfitPercent: Number(takeProfitPercent),
      period: a.period as "1M" | "3M" | "6M" | "1Y" | "Custom",
      startDate,
      endDate,
    },
  };
}

function isoDate(value: unknown, nullable: true): string | null | undefined;
function isoDate(value: unknown, nullable?: false): string | undefined;
function isoDate(value: unknown, nullable = false): string | null | undefined {
  if (value === null && nullable) return null;
  if (typeof value !== "string" || value.length < 20 || value.length > 40) return undefined;
  if (!/(?:Z|[+-]\d{2}:\d{2})$/i.test(value)) return undefined;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? value : undefined;
}

function safeMethodologyURL(value: unknown, locale: SharedResultLocale): string | null {
  if (typeof value !== "string" || Array.from(value).length > 160) return null;
  try {
    const url = new URL(value);
    const expectedPath = locale === "en" ? "/methodology/" : `/${locale}/methodology/`;
    if (
      url.protocol !== "https:" || url.hostname !== "backtest.click" || url.port
      || url.username || url.password || url.pathname !== expectedPath || url.search || url.hash
    ) return null;
    return url.toString();
  } catch {
    return null;
  }
}

export function resolveSharedResultLocale(language: string | null | undefined): SharedResultLocale {
  const candidates = (language || "")
    .split(",")
    .map((entry) => entry.split(";")[0]?.trim().replace(/_/g, "-").toLowerCase())
    .filter(Boolean) as string[];

  for (const candidate of candidates) {
    if (candidate.startsWith("pt")) return "pt-BR";
    if (candidate.startsWith("es")) return "es-419";
    const exact = sharedResultLocales.find((locale) => locale.toLowerCase() === candidate);
    if (exact) return exact;
    const base = sharedResultLocales.find(
      (locale) => locale.toLowerCase().split("-")[0] === candidate.split("-")[0],
    );
    if (base) return base;
  }
  return "en";
}

export function sharedResultTokenFromPath(pathname: string): string | null {
  const match = /^\/r\/([^/]+)\/?$/.exec(pathname);
  if (!match || !SHARE_RESULT_TOKEN_PATTERN.test(match[1])) return null;
  return match[1];
}

/** Parse and copy the public response into the strict allow-list above. */
export function parsePublicSharedResult(value: unknown): PublicSharedResult | null {
  if (!isRecord(value)) return null;
  if (value.schema_version !== 1
    || (value.mode !== "summary_only" && value.mode !== "reproducible_strategy")) return null;
  if (value.mode === "summary_only"
    && (value.share_id !== undefined || value.reproduction !== undefined)) return null;
  const locale = typeof value.locale === "string" && localeSet.has(value.locale)
    ? value.locale as SharedResultLocale
    : null;
  const createdAt = isoDate(value.created_at);
  const expiresAt = isoDate(value.expires_at);
  const directives = value.directives;
  const payload = value.payload;
  if (
    !locale || createdAt === undefined || expiresAt === undefined || !isRecord(directives)
    || directives.unlisted !== true || directives.robots !== "noindex,nofollow"
    || (directives.research_network_enabled !== undefined
      && typeof directives.research_network_enabled !== "boolean")
    || (directives.moderation_enabled !== undefined
      && typeof directives.moderation_enabled !== "boolean")
    || !isRecord(payload) || payload.schema_version !== 1
  ) return null;
  if (Date.parse(expiresAt) <= Date.parse(createdAt)) return null;

  const resultCreatedAt = isoDate(payload.result_created_at);
  const sharedAt = isoDate(payload.shared_at);
  const strategyLabel = safeNarrativeString(payload.strategy_label, 72);
  const symbol = safeString(payload.symbol, 32);
  const timeframe = safeString(payload.timeframe, 32);
  const testedPeriod = payload.tested_period;
  const evidenceState = payload.evidence_state;
  const metrics = payload.metrics;
  const equityCurve = payload.equity_curve;
  const omittedFields = payload.omitted_fields;
  const methodologyURL = safeMethodologyURL(payload.methodology_url, locale);
  const shareID = value.mode === "reproducible_strategy"
    && typeof value.share_id === "string" && safeShareIDPattern.test(value.share_id)
    ? value.share_id
    : undefined;
  const reproduction = value.mode === "reproducible_strategy"
    ? parseReproduction(value.reproduction)
    : undefined;
  if (
    resultCreatedAt === undefined || sharedAt === undefined || !strategyLabel || !symbol || !timeframe
    || !isRecord(testedPeriod) || !isRecord(metrics) || !Array.isArray(equityCurve)
    || equityCurve.length > SHARE_RESULT_MAX_EQUITY_POINTS
    || equityCurve.length === 1
    || !Array.isArray(omittedFields)
    || omittedFields.length !== requiredOmittedFields.size
    || new Set(omittedFields).size !== requiredOmittedFields.size
    || omittedFields.some((field) => typeof field !== "string" || !requiredOmittedFields.has(field))
    || !methodologyURL
    || (value.mode === "reproducible_strategy" && (!shareID || !reproduction))
    || typeof evidenceState !== "string" || !evidenceStates.has(evidenceState as SharedEvidenceState)
  ) return null;

  const periodStart = isoDate(testedPeriod.start, true);
  const periodEnd = isoDate(testedPeriod.end, true);
  const totalReturnPercent = finiteNumber(metrics.total_return_percent, -1_000_000_000, 1_000_000_000);
  const maxDrawdownPercent = finiteNumber(metrics.max_drawdown_percent, 0, 100);
  const winRatePercent = finiteNumber(metrics.win_rate_percent, 0, 100);
  const profitFactor = finiteNumber(metrics.profit_factor, 0, 1_000_000_000);
  const totalTrades = integer(metrics.total_trades, 0, 10_000_000);
  const riskNote = safeNarrativeString(payload.risk_note, 1_000);
  if (
    periodStart === undefined || periodEnd === undefined || totalReturnPercent === undefined
    || maxDrawdownPercent === undefined || winRatePercent === undefined
    || profitFactor === undefined || totalTrades === undefined || !riskNote
  ) return null;
  if (periodStart && periodEnd && Date.parse(periodEnd) < Date.parse(periodStart)) return null;
  if (reproduction && (
    reproduction.strategyLabel !== strategyLabel
    || reproduction.symbol !== symbol
    || reproduction.timeframe !== timeframe
    || (periodStart && Date.parse(reproduction.assumptions.startDate) !== Date.parse(periodStart))
    || (periodEnd && Date.parse(reproduction.assumptions.endDate) !== Date.parse(periodEnd))
  )) return null;

  const normalizedCurve: SharedEquityPoint[] = [];
  for (const [index, point] of equityCurve.entries()) {
    if (!isRecord(point)) return null;
    const progress = finiteNumber(point.progress, 0, 1);
    const returnPercent = finiteNumber(point.return_percent, -1_000_000_000, 1_000_000_000);
    if (progress == null || returnPercent == null) return null;
    if (index === 0 && (Math.abs(progress) > 1e-9 || Math.abs(returnPercent) > 1e-9)) return null;
    if (index > 0 && progress <= normalizedCurve[index - 1].progress) return null;
    normalizedCurve.push({ progress, returnPercent });
  }
  if (
    normalizedCurve.length
    && Math.abs(normalizedCurve[normalizedCurve.length - 1].progress - 1) > 1e-9
  ) return null;

  return {
    mode: value.mode,
    researchNetworkEnabled: directives.research_network_enabled === true,
    moderationEnabled: directives.moderation_enabled === true,
    ...(shareID ? { shareID } : {}),
    locale,
    createdAt,
    expiresAt,
    resultCreatedAt,
    strategyLabel,
    symbol,
    timeframe,
    testedPeriod: { start: periodStart, end: periodEnd },
    evidenceState: evidenceState as SharedEvidenceState,
    metrics: {
      totalReturnPercent,
      maxDrawdownPercent,
      winRatePercent,
      profitFactor,
      totalTrades,
    },
    equityCurve: normalizedCurve,
    methodologyURL,
    riskNote,
    ...(reproduction ? { reproduction } : {}),
  };
}

export function parseSharedResultBootstrap(value: unknown): SharedResultBootstrap | null {
  if (!isRecord(value) || value.schemaVersion !== 1 || !isRecord(value.state)) return null;
  if (value.state.kind === "ready") {
    // Bootstrap data is already normalized; serialize it through the public
    // envelope parser shape so an injected/static document cannot widen it.
    const result = value.state.result;
    if (!isRecord(result)) return null;
    const syntheticEnvelope = {
      schema_version: 1,
      share_id: result.shareID,
      locale: result.locale,
      mode: result.mode,
      created_at: result.createdAt,
      expires_at: result.expiresAt,
      reproduction: isRecord(result.reproduction) && isRecord(result.reproduction.assumptions)
        ? {
            schema_version: result.reproduction.schemaVersion,
            strategy_dsl: result.reproduction.strategyDSL,
            strategy_label: result.reproduction.strategyLabel,
            symbol: result.reproduction.symbol,
            timeframe: result.reproduction.timeframe,
            assumptions: {
              commission_percent: result.reproduction.assumptions.commissionPercent,
              slippage_bps: result.reproduction.assumptions.slippageBps,
              position_size_percent: result.reproduction.assumptions.positionSizePercent,
              execution_model: result.reproduction.assumptions.executionModel,
              margin_mode: result.reproduction.assumptions.marginMode,
              leverage: result.reproduction.assumptions.leverage,
              maintenance_margin_percent: result.reproduction.assumptions.maintenanceMarginPercent,
              funding_rate_percent: result.reproduction.assumptions.fundingRatePercent,
              funding_interval_hours: result.reproduction.assumptions.fundingIntervalHours,
              liquidation_fee_percent: result.reproduction.assumptions.liquidationFeePercent,
              trade_direction: result.reproduction.assumptions.tradeDirection,
              exit_mode: result.reproduction.assumptions.exitMode,
              stop_loss_percent: result.reproduction.assumptions.stopLossPercent,
              take_profit_percent: result.reproduction.assumptions.takeProfitPercent,
              period: result.reproduction.assumptions.period,
              start_date: result.reproduction.assumptions.startDate,
              end_date: result.reproduction.assumptions.endDate,
            },
          }
        : undefined,
      directives: {
        unlisted: true,
        robots: "noindex,nofollow",
        research_network_enabled: result.researchNetworkEnabled,
        moderation_enabled: result.moderationEnabled,
      },
      payload: {
        schema_version: 1,
        result_created_at: result.resultCreatedAt,
        shared_at: result.createdAt,
        strategy_label: result.strategyLabel,
        symbol: result.symbol,
        timeframe: result.timeframe,
        tested_period: result.testedPeriod,
        evidence_state: result.evidenceState,
        metrics: {
          total_return_percent: isRecord(result.metrics) ? result.metrics.totalReturnPercent : undefined,
          max_drawdown_percent: isRecord(result.metrics) ? result.metrics.maxDrawdownPercent : undefined,
          win_rate_percent: isRecord(result.metrics) ? result.metrics.winRatePercent : undefined,
          profit_factor: isRecord(result.metrics) ? result.metrics.profitFactor : undefined,
          total_trades: isRecord(result.metrics) ? result.metrics.totalTrades : undefined,
        },
        equity_curve: Array.isArray(result.equityCurve)
          ? result.equityCurve.map((point) => isRecord(point) ? {
              progress: point.progress,
              return_percent: point.returnPercent,
            } : point)
          : result.equityCurve,
        methodology_url: result.methodologyURL,
        risk_note: result.riskNote,
        omitted_fields: [...requiredOmittedFields],
      },
    };
    const normalized = parsePublicSharedResult(syntheticEnvelope);
    return normalized ? { schemaVersion: 1, state: { kind: "ready", result: normalized } } : null;
  }
  if (["not_found", "expired", "service_unavailable"].includes(String(value.state.kind))) {
    return {
      schemaVersion: 1,
      state: { kind: value.state.kind as "not_found" | "expired" | "service_unavailable" },
    };
  }
  return null;
}

export function safeBootstrapJSON(value: SharedResultBootstrap): string {
  return JSON.stringify(value)
    .replace(/&/g, "\\u0026")
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}
